import React, { Component, Fragment } from 'react';
import classnames from 'classnames';
import { Container } from 'reactstrap';
import { Loader } from '../../components/layout/Loader/Loader';
import { Header } from '../../components/layout/Navbars/Header';
import routes from '../../helpers/sideMenuEntries';
import { Sidebar } from '../../components/layout/Sidebar/Sidebar';
import logoSrc from '../../assets/images/logo.svg';
import {GlobalNotification} from "../../components/layout/Messages/GlobalNotification";
import { isNavigationPathActive } from '../../helpers/activeNavigation';

const SIDENAV_BREAKPOINT = 1200;
const MOBILE_SIDENAV_BREAKPOINT = 768;
const DESKTOP_SIDENAV_STORAGE_KEY = 'mysign.admin.desktopSidenavExpanded';
const DESKTOP_POINTER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
const SIDENAV_DESKTOP_CLASS = 'g-sidenav-desktop';
const SIDENAV_BODY_CLASSES = [
  'g-sidenav-hidden',
  'g-sidenav-pinned',
  'g-sidenav-show',
  SIDENAV_DESKTOP_CLASS
];

const getDesktopPointerMediaQuery = () => {
  try {
    return typeof window.matchMedia === 'function'
      ? window.matchMedia(DESKTOP_POINTER_MEDIA_QUERY)
      : null;
  } catch (error) {
    return null;
  }
};

const isMobileSidenavViewport = pointerMediaQuery => (
  window.innerWidth < MOBILE_SIDENAV_BREAKPOINT || (
    window.innerWidth < SIDENAV_BREAKPOINT && !(pointerMediaQuery && pointerMediaQuery.matches)
  )
);

const getDesktopSidenavPreference = () => {
  try {
    const storedPreference = window.localStorage.getItem(DESKTOP_SIDENAV_STORAGE_KEY);

    if (storedPreference === 'true' || storedPreference === 'false') {
      return storedPreference === 'true';
    }
  } catch (error) {
    // Storage can be unavailable in restricted browser contexts.
  }

  return true;
};

const setDesktopSidenavPreference = sidenavOpen => {
  try {
    window.localStorage.setItem(DESKTOP_SIDENAV_STORAGE_KEY, String(sidenavOpen));
  } catch (error) {
    // Keep the in-memory shell usable when storage is unavailable.
  }
};

class DashboardLayout extends Component {
  constructor(props) {
    super(props);

    this.pointerMediaQuery = getDesktopPointerMediaQuery();
    this.mobileViewport = isMobileSidenavViewport(this.pointerMediaQuery);
    this.state = {
      sidenavOpen: this.mobileViewport ? false : getDesktopSidenavPreference()
    };
  }

  mainContentRef = React.createRef();

  componentDidMount() {
    this.syncSidenavBodyClasses();
    window.addEventListener('resize', this.handleResize);
    this.addPointerModeListener();
  }

  componentDidUpdate(previousProps, previousState) {
    const previousPath = previousProps.location && previousProps.location.pathname;
    const currentPath = this.props.location && this.props.location.pathname;

    if (previousPath !== currentPath) {
      this.resetScrollPosition();

      if (this.isMobileViewport() && this.state.sidenavOpen) {
        this.closeSidenav();
      }
    }

    if (previousState.sidenavOpen !== this.state.sidenavOpen) {
      this.syncSidenavBodyClasses();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.removePointerModeListener();
    SIDENAV_BODY_CLASSES.forEach(className => document.body.classList.remove(className));
  }

  isMobileViewport = () => isMobileSidenavViewport(this.pointerMediaQuery);

  addPointerModeListener = () => {
    if (!this.pointerMediaQuery) {
      return;
    }

    if (typeof this.pointerMediaQuery.addEventListener === 'function') {
      this.pointerMediaQuery.addEventListener('change', this.handlePointerModeChange);
    } else if (typeof this.pointerMediaQuery.addListener === 'function') {
      this.pointerMediaQuery.addListener(this.handlePointerModeChange);
    }
  };

  removePointerModeListener = () => {
    if (!this.pointerMediaQuery) {
      return;
    }

    if (typeof this.pointerMediaQuery.removeEventListener === 'function') {
      this.pointerMediaQuery.removeEventListener('change', this.handlePointerModeChange);
    } else if (typeof this.pointerMediaQuery.removeListener === 'function') {
      this.pointerMediaQuery.removeListener(this.handlePointerModeChange);
    }
  };

  resetScrollPosition = () => {
    document.documentElement.scrollTop = 0;

    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }

    if (this.mainContentRef.current) {
      this.mainContentRef.current.scrollTop = 0;
    }
  };

  syncSidenavBodyClasses = () => {
    const { sidenavOpen } = this.state;

    document.body.classList.toggle('g-sidenav-pinned', sidenavOpen);
    document.body.classList.toggle('g-sidenav-show', sidenavOpen);
    document.body.classList.toggle('g-sidenav-hidden', !sidenavOpen);
    document.body.classList.toggle(SIDENAV_DESKTOP_CLASS, !this.isMobileViewport());
  };

  syncViewportMode = () => {
    const mobileViewport = this.isMobileViewport();

    if (mobileViewport === this.mobileViewport) {
      return;
    }

    this.mobileViewport = mobileViewport;

    if (mobileViewport) {
      if (this.state.sidenavOpen) {
        this.closeSidenav();
      } else {
        this.syncSidenavBodyClasses();
      }
      return;
    }

    const desktopSidenavOpen = getDesktopSidenavPreference();

    if (desktopSidenavOpen !== this.state.sidenavOpen) {
      this.setState({ sidenavOpen: desktopSidenavOpen });
    } else {
      this.syncSidenavBodyClasses();
    }
  };

  handleResize = () => this.syncViewportMode();

  handlePointerModeChange = () => this.syncViewportMode();

  getBrandText = path => {
    for (let i = 0; i < routes.length; i++) {
      if (isNavigationPathActive(path, routes[i].path, routes[i].activePaths)) {
        return routes[i].name;
      }
    }
    return "Brand";
  };
  // toggles collapse between mini sidenav and normal
  toggleSidenav = () => {
    const sidenavOpen = !this.state.sidenavOpen;

    if (!this.isMobileViewport()) {
      setDesktopSidenavPreference(sidenavOpen);
    }

    this.setState({ sidenavOpen });
  };

  // this is used on mobile devices, when a user navigates
  // the sidebar will autoclose
  closeSidenav = () => {
    if (this.state.sidenavOpen) {
      this.setState({ sidenavOpen: false });
    }
  };

  render() {
    return (
      <Fragment>
        <Loader align="text-center" verticalAlign="middle" />
        <Sidebar
          {...this.props}
          routes={routes}
          toggleSidenav={this.toggleSidenav}
          sidenavOpen={this.state.sidenavOpen}
          logo={{
            innerLink: "/",
            imgSrc: logoSrc,
            imgAlt: "REEA Logo"
          }}
        />
        <div className="main-content dashboard-layout__main h-100" ref={this.mainContentRef}>
          <Header
            {...this.props}
            toggleSidenav={ this.toggleSidenav }
            sidenavOpen={ this.state.sidenavOpen }
            brandText={ this.getBrandText(this.props.location.pathname) }
          />
          <Container
            className={classnames(
              'dashboard-layout__content mw-100 mx-0 px-0',
              this.props.className,
              'dashboard-layout__content--workspace'
            )}>
            {this.props.children}
          </Container>
        </div>
        {this.isMobileViewport() && this.state.sidenavOpen && (
          <button
            aria-label="Close navigation"
            className="dashboard-layout__backdrop d-xl-none"
            onClick={this.closeSidenav}
            type="button"
          />
        )}
        <GlobalNotification/>
      </Fragment>
    );
  }
}

DashboardLayout.defaultProps = {
  className: '',
  location: {
    pathname: ''
  }
};

export default DashboardLayout;

export { DESKTOP_SIDENAV_STORAGE_KEY };
